import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DropDown from "@components/ui/dropdown/DropDown";
import OrdreStatusDropdown from "@components/ui/dropdown/OrdreStatusDropdown";
import AssignDropdown from "@components/ui/dropdown/AssignDropdown";
import InlineTagEditor from "@components/ui/tags/InlineTagEditor";
import DatePicker from "@components/ui/selection/DatePicker";
import DateTimePicker from "@components/ui/selection/DateTimePicker";
import CheckBox from "@components/ui/selection/CheckBox";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { useState, useRef, useEffect, useMemo } from "react";
import Avvvatars from "avvvatars-react";
import ChatPanel from "@components/chat/ChatPanel";
import { ordresApi } from "@services/api/ordres";
import { DocumentPreviewModal } from "@components/ui/DocumentPreviewModal";
import { dossiersApi } from "@services/api/dossiers";
import { useOrdreStore } from "@store/ordreStore";
import { usePlanningStore } from "@store/planningStore";
import { navigateTo } from "context/navigation.service";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import VehicleImage from "@components/ui/VehicleImage";
import VehicleEditModal from "@components/modals/VehicleEditModal";
import ContactFormModal from "@components/modals/ContactFormModal";
import type {
  Ordre,
  OrdreStatus,
  OrdreIntervention,
  OrdreDocument,
} from "types/ordre";
import type { DossierType } from "types/dossier";
import type { Contact } from "types/contact";
import type { Dossier } from "types/dossier";
import type { ClientVehicle } from "types/client_vehicle";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { atelierApi } from "@services/api/atelier";
import RestitutionChangeConfirmModal from "@components/modals/RestitutionChangeConfirmModal";
import SecondaryOrdreModal from "@components/modals/SecondaryOrdreModal";

// ─── Constants ─────────────────────────────────────────────

const TABS = [
  { value: "ordre", label: "Ordre" },
  { value: "interventions", label: "Interventions" },
  { value: "contacts", label: "Contacts" },
  { value: "vehicules", label: "Véhicules" },
  { value: "documents", label: "Documents" },
  { value: "communications", label: "Chat" },
];

const STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commandé" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
];


function formatHour(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h${mins.toString().padStart(2, "0")}`;
}

function formatTimestamp(ts?: { seconds: number }): string {
  if (!ts) return "—";
  return new Date(ts.seconds * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ─── Sub-modal for adding items ────────────────────────────

function AddSubModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <BaseModal
      title={title}
      startIcon={<Icon1 name="add" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">{children}</div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label="Ajouter" onClick={onClose} />
      </div>
    </BaseModal>
  );
}

// ─── Tab content components ────────────────────────────────

function OrdreTab({
  form,
  update,
  assignedWorkers,
  linkedVehicle,
  dossierType,
  onDossierTypeChange,
}: {
  form: Ordre;
  update: <K extends keyof Ordre>(key: K, value: Ordre[K]) => void;
  assignedWorkers: { name: string; specialty: string }[];
  linkedVehicle?: ClientVehicle | null;
  dossierType?: DossierType;
  onDossierTypeChange?: (type: DossierType | undefined) => void;
}) {
  // Auto-fill modèle from linked vehicle when vehicle changes
  const prevVehicleIdRef = useRef(linkedVehicle?.id);
  useEffect(() => {
    if (linkedVehicle && linkedVehicle.id !== prevVehicleIdRef.current) {
      const vehicleModele = [linkedVehicle.marque, linkedVehicle.modele].filter(Boolean).join(" ");
      if (vehicleModele) {
        update("modele", vehicleModele);
      }
    }
    prevVehicleIdRef.current = linkedVehicle?.id;
  }, [linkedVehicle?.id]);

  // Auto-set restitution when réception date (day) changes.
  // If only time changed (same day): only update restitution if reception time
  // goes past the current restitution time on that same day.
  const prevReceptionRef = useRef(form.reception_date?.seconds);
  useEffect(() => {
    const receptionSeconds = form.reception_date?.seconds;
    const prevSeconds = prevReceptionRef.current;
    if (
      receptionSeconds &&
      receptionSeconds !== prevSeconds
    ) {
      const receptionDate = new Date(receptionSeconds * 1000);
      const prevDate = prevSeconds ? new Date(prevSeconds * 1000) : null;
      const sameDayAsBefore =
        prevDate &&
        receptionDate.getFullYear() === prevDate.getFullYear() &&
        receptionDate.getMonth() === prevDate.getMonth() &&
        receptionDate.getDate() === prevDate.getDate();

      if (sameDayAsBefore) {
        // Only time changed — adjust restitution only if reception goes past it
        // and restitution is on the same day
        const restitutionSeconds = form.restitution?.seconds;
        if (restitutionSeconds) {
          const restitutionDate = new Date(restitutionSeconds * 1000);
          const sameDay =
            receptionDate.getFullYear() === restitutionDate.getFullYear() &&
            receptionDate.getMonth() === restitutionDate.getMonth() &&
            receptionDate.getDate() === restitutionDate.getDate();
          if (sameDay && receptionSeconds >= restitutionSeconds) {
            const twoHoursInSeconds = 2 * 60 * 60;
            update("restitution", { seconds: receptionSeconds + twoHoursInSeconds });
          }
        }
      } else {
        // Day changed — reset restitution to reception + 2h
        const twoHoursInSeconds = 2 * 60 * 60;
        update("restitution", { seconds: receptionSeconds + twoHoursInSeconds });
      }
    }
    prevReceptionRef.current = receptionSeconds;
  }, [form.reception_date?.seconds]);


  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Status + Assign + Tags — compact row like OrdreDetailScreen */}
      <div className="flex flex-row items-center gap-2 flex-wrap">
        <OrdreStatusDropdown
          currentStatus={form.status}
          onStatusChange={(v) => update("status", v)}
        />
        <AssignDropdown
          assignedTo={form.assigned_to}
          onAssign={(userId) => update("assigned_to", userId || "")}
        />
        {assignedWorkers.map((w) => (
          <span
            key={w.name}
            className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1"
            style={{
              background: w.specialty === "carrosserie" ? "#FEF3C7" : w.specialty === "mecanique" ? "#DBEAFE" : "#F3F4F6",
              color: w.specialty === "carrosserie" ? "#92400E" : w.specialty === "mecanique" ? "#1E40AF" : "#374151",
            }}
          >
            <Icon1 name="side-ordres" size={12} color={w.specialty === "carrosserie" ? "#92400E" : w.specialty === "mecanique" ? "#1E40AF" : "#374151"} />
            {w.name}
          </span>
        ))}
        {form.amount != null && (
          <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
            {form.amount.toFixed(2)} € TTC
          </span>
        )}
      </div>
      <InlineTagEditor
        scope="ordre"
        tagIds={form.tag_ids || []}
        onSave={(ids) => update("tag_ids", ids)}
      />

      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="N° Ordre" value={form.num} onChange={(v) => update("num", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Nom" value={form.name} onChange={(v) => update("name", v)} />
        </div>
      </div>
      <DropDown
        label="Type de dossier"
        value={dossierType || ""}
        options={[
          { value: "", label: "Non défini" },
          { value: "particulier", label: "Particulier" },
          { value: "professionnel", label: "Professionnel" },
          { value: "loueur", label: "Loueur" },
          { value: "garantie", label: "Garantie" },
          { value: "occasion", label: "Véhicule occasion" },
          { value: "interne", label: "Véhicule interne" },
        ]}
        onSelect={(v) => onDossierTypeChange?.((v || undefined) as DossierType | undefined)}
      />
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Immatriculation" value={form.immatriculation || ""} onChange={(v) => update("immatriculation", v)} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={form.modele || ""} onChange={(v) => update("modele", v)} />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DateTimePicker
            label="Date réception"
            seconds={form.reception_date?.seconds || null}
            onChange={(s) => {
              update("reception_date", { seconds: s });
              if (form.restitution?.seconds && s > form.restitution.seconds) {
                update("restitution", { seconds: s });
              }
            }}
          />
        </div>
        <div className="flex-1">
          <DateTimePicker
            label="Date restitution"
            seconds={form.restitution?.seconds || null}
            onChange={(s) => {
              const minSeconds = form.reception_date?.seconds || 0;
              update("restitution", { seconds: Math.max(s, minSeconds) });
            }}
          />
        </div>
      </div>
      {!form.restitution?.seconds && (
        <span className="text-h4 text-red-500">La date de restitution est obligatoire</span>
      )}
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Montant TTC (€)" type="number" value={form.amount != null ? String(form.amount) : ""} onChange={(v) => update("amount", v ? parseFloat(v) : undefined)} />
        </div>
        <div className="flex-1">
          <TextField label="Campagne" value={form.campagne || ""} onChange={() => {}} disabled />
        </div>
      </div>
      <AreaTextField
        label="Notes"
        value={form.notes || ""}
        onChange={(v) => update("notes", v)}
        rows={3}
      />
    </div>
  );
}

// ─── Intervention Picker Modal ─────────────────────────────

const INTERVENTION_TYPE_LABELS: Record<string, string> = {
  entretien: "Entretien / Révision", reparation: "Réparation / Panne", diagnostic: "Diagnostic",
  controle_technique: "Contrôle technique", pneumatique: "Pneus", carrosserie: "Carrosserie / Vitrage",
};
const INTERVENTION_TYPE_OPTIONS = Object.entries(INTERVENTION_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

function InterventionPickerModal({ onClose, onAdd, configuredInterventions, onCatalogUpdated, group = "initial" }: {
  onClose: () => void;
  onAdd: (inter: OrdreIntervention) => void;
  configuredInterventions: Intervention[];
  onCatalogUpdated: (items: Intervention[]) => void;
  group?: string;
}) {
  const orgData = useDataStore((s) => s.orgData);
  const workers = useAtelierStore((s) => s.workers);
  const workerOptions = [
    { value: "", label: "Non assigné" },
    ...workers.map((w) => ({ value: w.id, label: w.name })),
  ];

  const [mode, setMode] = useState<"catalog" | "new">("catalog");
  const [selectedCatalog, setSelectedCatalog] = useState<Intervention | null>(null);
  const [label, setLabel] = useState("");
  const [defaultH, setDefaultH] = useState("");
  const [estimatedH, setEstimatedH] = useState("");
  const [interventionType, setInterventionType] = useState("entretien");
  const [assignedTo, setAssignedTo] = useState("");
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setSelectedCatalog(null); setLabel(""); setDefaultH(""); setEstimatedH(""); setAssignedTo("");
  };

  const handleSelectCatalog = (item: Intervention) => {
    setSelectedCatalog(item);
    const h = +(item.estimated_minutes / 60).toFixed(2);
    setLabel(item.name);
    setDefaultH(String(h));
    setEstimatedH(String(h));
    setAssignedTo("");
  };

  const canAdd = label.trim() !== "" && (mode === "catalog" ? selectedCatalog !== null : true);

  const handleAdd = async () => {
    if (!canAdd) return;
    setSaving(true);
    try {
      if (mode === "new" && orgData) {
        const minutes = Math.round((parseFloat(estimatedH) || parseFloat(defaultH) || 0) * 60);
        const created = await interventionsApi.create({
          org_id: orgData.id,
          name: label,
          estimated_minutes: minutes,
          min_days_margin: 0,
          type: interventionType,
          active: true,
          fast: false,
        });
        onCatalogUpdated([...configuredInterventions, created]);
      }
      onAdd({
        id: `i-${Date.now()}`,
        label,
        default_hours: parseFloat(defaultH) || 0,
        estimated_hours: parseFloat(estimatedH) || parseFloat(defaultH) || 0,
        assigned_to: assignedTo,
        group,
      });
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal title="Ajouter une intervention" startIcon={<Icon1 name="plus" size={20} />} onClose={onClose} width="max-w-lg">
      <div className="p-4 flex flex-col gap-4">
        <ChunkyTabs
          activeTab={mode}
          tabs={[{ value: "catalog", label: "Depuis le catalogue" }, { value: "new", label: "Nouvelle" }]}
          setActiveTab={(t) => { setMode(t as "catalog" | "new"); resetForm(); }}
        />

        {mode === "catalog" && (
          <div className="flex flex-col gap-3">
            <div className="overflow-y-auto rounded-lg border border-neutral-200" style={{ maxHeight: 220 }}>
              {Object.entries(INTERVENTION_TYPE_LABELS).map(([cat, catLabel]) => {
                const items = configuredInterventions.filter((i) => i.type === cat && i.active).sort((a, b) => a.name.localeCompare(b.name, "fr"));
                if (!items.length) return null;
                return (
                  <div key={cat}>
                    <div className="px-3 py-1 text-h5 font-semibold text-neutral-400 bg-neutral-50 sticky top-0">{catLabel}</div>
                    {items.map((item) => {
                      const h = +(item.estimated_minutes / 60).toFixed(2);
                      const isSelected = selectedCatalog?.id === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleSelectCatalog(item)}
                          className={`flex flex-row items-center justify-between w-full px-3 py-2 text-left transition-colors ${isSelected ? "bg-indigo-50" : "hover:bg-neutral-50"}`}
                        >
                          <span className={`text-h3 ${isSelected ? "text-indigo-700 font-medium" : "text-neutral-600"}`}>{item.name}</span>
                          <span className="text-h4 text-neutral-400 ml-2 flex-shrink-0">{h}h</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {configuredInterventions.filter((i) => i.active).length === 0 && (
                <div className="px-3 py-6 text-h3 text-neutral-400 text-center">Aucune intervention dans le catalogue</div>
              )}
            </div>
            {selectedCatalog && (
              <div className="flex flex-col gap-3 pt-2 border-t border-neutral-100">
                <TextField label="Libellé" value={label} onChange={setLabel} />
                <div className="flex flex-row gap-2">
                  <div className="flex-1">
                    <TextField label="Heures estimées" type="number" value={estimatedH} onChange={setEstimatedH} />
                  </div>
                  <div className="flex-1">
                    <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === "new" && (
          <div className="flex flex-col gap-3">
            <TextField label="Libellé" value={label} onChange={setLabel} />
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Durée défaut (h)" type="number" value={defaultH} onChange={setDefaultH} />
              </div>
              <div className="flex-1">
                <TextField label="Durée estimée (h)" type="number" value={estimatedH} onChange={setEstimatedH} />
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <DropDown label="Type" options={INTERVENTION_TYPE_OPTIONS} value={interventionType} onSelect={(v) => setInterventionType(String(v))} />
              </div>
              <div className="flex-1">
                <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
              </div>
            </div>
            <span className="text-h4 text-neutral-400">Cette intervention sera ajoutée à votre catalogue.</span>
          </div>
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={saving ? "Enregistrement..." : "Ajouter"} onClick={handleAdd} disabled={!canAdd || saving} />
      </div>
    </BaseModal>
  );
}

// ContactFormModal is now imported from @components/modals/ContactFormModal

function AddVehicleModal({ onClose }: { onClose: () => void }) {
  const [plate, setPlate] = useState("");
  const [marque, setMarque] = useState("");
  const [modele, setModele] = useState("");
  const [vin, setVin] = useState("");
  return (
    <AddSubModal title="Ajouter un véhicule" onClose={onClose}>
      <TextField label="Immatriculation" value={plate} onChange={setPlate} />
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <TextField label="Marque" value={marque} onChange={setMarque} />
        </div>
        <div className="flex-1">
          <TextField label="Modèle" value={modele} onChange={setModele} />
        </div>
      </div>
      <TextField label="VIN" value={vin} onChange={setVin} />
    </AddSubModal>
  );
}

// ─── Tab content components (Interventions, Atelier, Contacts, Vehicules, Documents) ──

const GROUP_STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  devis_sent: "Devis envoye",
  devis_signed: "Devis signe",
  ready: "Pret",
};

const GROUP_STATUS_COLORS: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-500",
  devis_sent: "bg-blue-100 text-blue-700",
  devis_signed: "bg-amber-100 text-amber-700",
  ready: "bg-green-100 text-green-700",
};

interface AtelierChargeDisplay {
  id: string;
  worker_name: string;
  specialty: string;
  date: string;
  start_hour: number;
  duration_hours: number;
  is_placed: boolean;
}

function ModalGroupSection({
  title,
  group,
  interventions,
  groupStatus,
  onAdd,
  onRemove,
}: {
  title: string;
  group: string;
  interventions: OrdreIntervention[];
  groupStatus?: { status: string };
  onAdd: (group: string) => void;
  onRemove: (idx: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const status = groupStatus?.status || "draft";
  const workers = useAtelierStore((s) => s.workers);
  const getWorkerName = (id?: string) => {
    if (!id) return "Non assigné";
    const w = workers.find((w) => w.id === id);
    return w ? w.name : id;
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-2">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-medium">{title}</span>
          <span className="text-h4 text-neutral-400">({interventions.length})</span>
        </div>
        <span className={`text-h5 font-medium px-2 py-0.5 rounded-full ${GROUP_STATUS_COLORS[status] || ""}`}>
          {GROUP_STATUS_LABELS[status] || status}
        </span>
      </div>
      {!collapsed && (
        <div className="p-3 flex flex-col gap-2">
          {interventions.map((inter, i) => (
            <div key={inter.id || i} className="flex flex-row items-center justify-between bg-white rounded-lg border border-neutral-200 px-3 py-2">
              <div className="flex flex-col">
                <span className="text-h3 font-medium">{inter.label || "Nouvelle intervention"}</span>
                <span className="text-h4 text-neutral-400">
                  {inter.estimated_hours || inter.default_hours}h — {getWorkerName(inter.assigned_to)}
                </span>
              </div>
              <button onClick={() => onRemove(i)} className="p-1 rounded hover:bg-neutral-100 transition-colors flex-shrink-0">
                <Icon1 name="close" size={14} color="#A1A1A1" />
              </button>
            </div>
          ))}
          {interventions.length === 0 && (
            <span className="text-h3 text-neutral-400 py-3 text-center">Aucune intervention</span>
          )}
          <div className="mt-1">
            <SecondaryButton variant="small" startIcon={<Icon1 name="plus" size={14} />} label="Ajouter" onClick={() => onAdd(group)} />
          </div>
        </div>
      )}
    </div>
  );
}

const SPECIALTY_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  mecanique:     { bg: "#DBEAFE", color: "#1E40AF", label: "Méca" },
  carrosserie:   { bg: "#FEF3C7", color: "#92400E", label: "Carross." },
  peinture:      { bg: "#FEE2E2", color: "#DC2626", label: "Peinture" },
  diagnostique:  { bg: "#D1FAE5", color: "#059669", label: "Diag." },
  autre:         { bg: "#F3F4F6", color: "#374151", label: "Autre" },
};

function AtelierPlanningSection({ charges }: { charges: AtelierChargeDisplay[] }) {
  const [collapsed, setCollapsed] = useState(false);
  const placed   = charges.filter((c) => c.is_placed);
  const unplaced = charges.filter((c) => !c.is_placed);

  const statusBadge = charges.length === 0 ? null : unplaced.length > 0
    ? <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{unplaced.length} a planifier</span>
    : <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Planifie</span>;

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-2">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-medium">Planification atelier</span>
          <span className="text-h4 text-neutral-400">({charges.length})</span>
          {statusBadge}
        </div>
      </div>

      {!collapsed && (
        <div className="divide-y divide-neutral-100">
          {charges.length === 0 && (
            <div className="px-3 py-4 text-center text-h3 text-neutral-400">Aucune charge atelier</div>
          )}

          {placed.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-3 py-2">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {c.worker_name}
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h3 text-neutral-600">{c.date.split("-").reverse().join("/")}</span>
                <span className="text-h3 text-neutral-500">{formatHour(c.start_hour)} — {formatHour(c.start_hour + c.duration_hours)}</span>
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
              </div>
            );
          })}

          {unplaced.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-3 py-2">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                  A planifier
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InterventionsTab({
  interventions,
  interventionGroups,
  atelierCharges,
  onAdd,
  onRemove,
}: {
  interventions: Ordre["interventions"];
  interventionGroups?: Ordre["intervention_groups"];
  atelierCharges: AtelierChargeDisplay[];
  onAdd: (group: string) => void;
  onRemove: (idx: number) => void;
}) {
  // Compute additional group keys
  const additionalKeys = useMemo(() => {
    const keys = new Set<string>();
    if (interventionGroups) {
      Object.keys(interventionGroups).filter((k) => k.startsWith("additional_")).forEach((k) => keys.add(k));
    }
    interventions.forEach((i) => { if (i.group?.startsWith("additional_")) keys.add(i.group); });
    // Legacy compat
    if (interventionGroups?.additional || interventions.some((i) => i.group === "additional")) {
      keys.add("additional_0");
    }
    return Array.from(keys).sort();
  }, [interventionGroups, interventions]);

  const initialInterventions = interventions.filter((i) => (i.group || "initial") === "initial");

  const getAdditionalInterventions = (key: string) =>
    interventions.filter((i) =>
      i.group === key || (key === "additional_0" && i.group === "additional"),
    );

  const getGroupStatus = (key: string) => {
    if (!interventionGroups) return undefined;
    return interventionGroups[key] || (key === "additional_0" ? interventionGroups["additional"] : undefined);
  };

  return (
    <div className="flex flex-col p-4 gap-3">
      <ModalGroupSection
        title="Interventions initiales"
        group="initial"
        interventions={initialInterventions}
        groupStatus={interventionGroups?.initial}
        onAdd={onAdd}
        onRemove={(i) => {
          const inter = initialInterventions[i];
          const globalIdx = interventions.findIndex((x) => x.id === inter.id);
          if (globalIdx >= 0) onRemove(globalIdx);
        }}
      />

      {additionalKeys.map((key, idx) => {
        const groupInterventions = getAdditionalInterventions(key);
        return (
          <ModalGroupSection
            key={key}
            title={`Interventions supplementaires${additionalKeys.length > 1 ? ` #${idx + 1}` : ""}`}
            group={key}
            interventions={groupInterventions}
            groupStatus={getGroupStatus(key)}
            onAdd={onAdd}
            onRemove={(i) => {
              const inter = groupInterventions[i];
              const globalIdx = interventions.findIndex((x) => x.id === inter.id);
              if (globalIdx >= 0) onRemove(globalIdx);
            }}
          />
        );
      })}

      <AtelierPlanningSection charges={atelierCharges} />
    </div>
  );
}

function ContactsTab({ contacts, onAdd, onEdit }: { contacts: Ordre["contacts"]; onAdd: () => void; onEdit: (contact: Contact) => void }) {
  return (
    <div className="flex flex-col p-4 gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Contacts</span>
        <SecondaryButton variant="small" onClick={onAdd}>
          <span className="flex flex-row items-center gap-1">
            <Icon1 name="plus" size={12} />
            Ajouter
          </span>
        </SecondaryButton>
      </div>
      {(contacts || []).map((c, i) => {
        const displayName = [c.prenom, c.nom].filter(Boolean).join(" ") || "—";
        return (
          <div key={i} className="flex flex-row items-center gap-3 bg-white rounded-lg border border-neutral-200 px-3 py-2">
            <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
              <Avvvatars value={displayName} style="shape" size={32} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>{displayName}</span>
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{c.role || "—"} · {c.phone || c.email || "—"}</span>
            </div>
            <button
              onClick={() => onEdit(c)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}
      {(!contacts || contacts.length === 0) && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun contact</span>
      )}
    </div>
  );
}

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" }, { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" }, { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" }, { value: "vert", label: "Vert" },
  { value: "autre", label: "Autre" },
];

// VehicleEditModal is now imported from @components/modals/VehicleEditModal

function VehiculesTab({
  dossierVehicles,
  selectedVehicleId,
  onSelect,
  onEdit,
}: {
  dossierVehicles: Array<{ id: string; plate?: string; marque?: string; modele?: string; coloris?: string; vin?: string; km?: number; mec?: number }>;
  selectedVehicleId?: string;
  onSelect: (vehicleId: string) => void;
  onEdit: (vehicle: any) => void;
}) {

  return (
    <div className="flex flex-col p-4 gap-2">
      <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Véhicules du dossier</span>
      {dossierVehicles.map((v) => {
        const isSelected = v.id === selectedVehicleId;
        return (
          <div
            key={v.id}
            className="flex flex-row items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
            style={{
              borderColor: isSelected ? "#5C6BFF" : "#E5E5E5",
              background: isSelected ? "#F5F6FF" : "#FFFFFF",
            }}
          >
            <button
              className="flex flex-row items-center gap-3 flex-1 min-w-0 text-left"
              onClick={() => onSelect(v.id)}
            >
              <div
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{
                  borderColor: isSelected ? "#5C6BFF" : "#D4D4D4",
                  background: isSelected ? "#5C6BFF" : "transparent",
                }}
              >
                {isSelected && (
                  <svg width={10} height={8} viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="#FFFFFF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div className="w-16 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} alt={v.plate} className="w-full h-full" iconSize={16} />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h3 font-medium truncate" style={{ fontFamily: "Poppins" }}>
                  {v.marque} {v.modele}
                </span>
                <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>
                  {v.plate || "—"}{v.km ? ` · ${v.km.toLocaleString()} km` : ""}
                </span>
              </div>
            </button>
            <button
              onClick={() => onEdit(v)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}
      {dossierVehicles.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun véhicule dans le dossier</span>
      )}
    </div>
  );
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

function PhotoThumbnail({ doc, onClick }: { doc: { id: string; name: string; gcs_path?: string }; onClick: () => void }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!doc.gcs_path) return;
    const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
    const parts = doc.gcs_path.split("/");
    const parentId = parts[2];
    (isOrdreDoc ? ordresApi.getDocumentUrl(parentId, doc.id) : dossiersApi.getDocumentUrl(parentId, doc.id))
      .then((url) => { if (!cancelled) setThumbUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id]);

  return (
    <div
      className="w-[100px] h-[100px] rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
      onClick={onClick}
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={20} color="#D4D4D4" />
      )}
    </div>
  );
}


function DocumentsTab({
  documents,
  onAdd,
  onPreview,
}: {
  documents: Array<{ id?: string; name: string; type: string; created_at?: string; gcs_path?: string }>;
  onAdd: () => void;
  onPreview: (doc: any) => void;
}) {
  const photos = documents.filter((d) => d.id && isImageDoc(d as any));
  const files = documents.filter((d) => !isImageDoc(d as any));

  return (
    <div className="flex flex-col p-4 gap-4">
      {/* Photo gallery */}
      {photos.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-h3 font-medium text-neutral-500">Photos ({photos.length})</span>
          <div className="flex flex-row flex-wrap gap-2">
            {photos.map((p) => (
              <PhotoThumbnail key={p.id} doc={p as any} onClick={() => onPreview(p)} />
            ))}
          </div>
        </div>
      )}

      {/* Files list */}
      {files.length > 0 && (
        <div className="flex flex-col gap-1">
          {photos.length > 0 && <span className="text-h3 font-medium text-neutral-500 mb-1">Documents ({files.length})</span>}
          {files.map((d, i) => (
            <div
              key={d.id ?? i}
              className="flex flex-row items-center gap-3 px-2 py-2 rounded-lg hover:bg-neutral-50 cursor-pointer"
              onClick={() => onPreview(d)}
            >
              <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                <Icon1 name="file" size={14} color="#A1A1A1" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h3 font-medium truncate">{d.name}</span>
                {d.created_at && <span className="text-h4 text-neutral-400">{d.created_at}</span>}
              </div>
              <button onClick={(e) => { e.stopPropagation(); onPreview(d); }} className="p-1 hover:bg-neutral-100 rounded flex-shrink-0">
                <Icon1 name="download" size={14} color="#A1A1A1" />
              </button>
            </div>
          ))}
        </div>
      )}

      {documents.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun document</span>
      )}

      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50"
        onClick={onAdd}
      >
        <Icon1 name="plus" size={14} color="#525252" />
        Ajouter un document
      </button>
    </div>
  );
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "autre", label: "Autre" },
];

function AddDocumentModal({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);

    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      if (result.suggested_name) {
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch {
      // Classification failed — user can still manually select type
    } finally {
      setClassifying(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await dossiersApi.uploadDocument(dossierId, file, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal
      title="Ajouter un document"
      startIcon={<Icon1 name="upload" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Drop zone */}
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
            dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
            }}
          />
        </div>

        {/* AI classification */}
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document...</span>
          </div>
        )}

        <TextField label="Nom du document" value={docName} onChange={setDocName} />
        <DropDown
          label="Type de document"
          options={DOCUMENT_TYPE_OPTIONS}
          value={docType}
          onSelect={(v) => setDocType(String(v))}
        />
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Main OrdreModal ───────────────────────────────────────

interface OrdreModalProps {
  ordre: Ordre;
  onClose: () => void;
  onSave: (ordre: Ordre) => void;
  onDelete?: (ordreId: string) => void;
}

export default function OrdreModal({ ordre, onClose, onSave, onDelete }: OrdreModalProps) {
  const [activeTab, setActiveTab] = useState("ordre");
  const [form, setForm] = useState<Ordre>({ ...ordre });
  const [subModal, setSubModal] = useState<string | null>(null);
  const [addGroup, setAddGroup] = useState<string>("initial");
  const [showRestitutionConfirm, setShowRestitutionConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAutoPlace, setShowAutoPlace] = useState<"confirm" | "results" | null>(null);
  const [autoPlaceLoading, setAutoPlaceLoading] = useState(false);
  const [autoPlaceResults, setAutoPlaceResults] = useState<{
    placed: { intervention_id: string; label: string; worker_name: string; date?: string; start_hour?: number; duration_hours?: number }[];
    unplaced: { intervention_id: string; label: string; worker_name: string; reason?: string }[];
  } | null>(null);

  const update = <K extends keyof Ordre>(key: K, value: Ordre[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);
  useEffect(() => {
    if (ordre.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre.dossier_id]);

  const orgData = useDataStore((s) => s.orgData);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const atelierJobs = useAtelierStore((s) => s.jobs);
  const atelierWorkers = useAtelierStore((s) => s.workers);
  const atelierCharges = useAtelierStore((s) => s.charges);

  const [configuredInterventions, setConfiguredInterventions] = useState<Intervention[]>([]);

  useEffect(() => {
    if (orgData?.id) {
      fetchAtelierData(orgData.id);
      interventionsApi.list(orgData.id).then(setConfiguredInterventions).catch(() => {});
    }
  }, [orgData?.id]);

  const ordreAtelierCharges = useMemo(() => {
    const workerMap = new Map(atelierWorkers.map((w) => [w.id, w.name]));
    // Placed jobs (on the grid)
    const jobRows: AtelierChargeDisplay[] = atelierJobs
      .filter((j) => j.ordre_id === ordre.id)
      .map((j) => ({
        id: j.id,
        worker_name: workerMap.get(j.workerId) || "—",
        specialty: j.specialty,
        date: j.date,
        start_hour: j.startHour,
        duration_hours: j.durationHours,
        is_placed: true,
      }));
    // Unplaced charges (still in sidebar)
    const chargeRows: AtelierChargeDisplay[] = atelierCharges
      .filter((c) => c.ordre_id === ordre.id)
      .map((c) => ({
        id: c.id,
        worker_name: "Non assigne",
        specialty: c.specialty,
        date: "—",
        start_hour: 0,
        duration_hours: c.hours,
        is_placed: false,
      }));
    return [...jobRows, ...chargeRows];
  }, [atelierJobs, atelierWorkers, atelierCharges, ordre.id]);

  const assignedWorkers = useMemo(() => {
    const seen = new Set<string>();
    return atelierJobs
      .filter((j) => j.ordre_id === ordre.id)
      .reduce<{ name: string; specialty: string }[]>((acc, j) => {
        if (!seen.has(j.workerId)) {
          seen.add(j.workerId);
          const w = atelierWorkers.find((w) => w.id === j.workerId);
          if (w) acc.push({ name: w.name, specialty: w.specialty });
        }
        return acc;
      }, []);
  }, [atelierJobs, atelierWorkers, ordre.id]);

  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [pendingContactData, setPendingContactData] = useState<{ contact: Contact | null; data: Partial<Contact> } | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<ClientVehicle | null>(null);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: any; url: string } | null>(null);

  const openDocPreview = async (doc: { id?: string; gcs_path?: string; name: string }) => {
    if (!doc.id || !doc.gcs_path) return;
    try {
      const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
      const parts = doc.gcs_path.split("/");
      const parentId = parts[2];
      const url = isOrdreDoc
        ? await ordresApi.getDocumentUrl(parentId, doc.id)
        : await dossiersApi.getDocumentUrl(parentId, doc.id);
      setPreviewDoc({ doc, url });
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'aperçu" });
    }
  };

  const saveContactToDossier = async (contact: Contact | null, data: Partial<Contact>) => {
    if (!parentDossier) return;
    setContactSaving(true);
    try {
      let updatedContacts: Contact[];
      if (contact) {
        // Editing existing contact
        updatedContacts = (parentDossier.contacts || []).map((c) =>
          c.id === contact.id ? { ...c, ...data } : c
        );
      } else {
        // Adding new contact
        const newContact: Contact = {
          id: `c-new-${Date.now()}`,
          dossier_id: parentDossier.id,
          nom: data.nom || "",
          is_primary: data.is_primary || false,
          created_at: new Date().toISOString(),
          ...data,
        };
        updatedContacts = [...(parentDossier.contacts || []), newContact];
      }
      const updatedDossier = await dossiersApi.update(parentDossier.id, { contacts: updatedContacts } as any);
      setParentDossier(updatedDossier);
      // Re-fetch enriched ordre to update form.contacts
      const refreshedOrdre = await ordresApi.get(ordre.id);
      setForm((prev) => ({ ...prev, contacts: refreshedOrdre.contacts }));
      useOrdreStore.getState().updateOrdre(refreshedOrdre);
      setSubModal(null);
      setEditingContact(null);
      setPendingContactData(null);
      showNotification({ type: "success", message: contact ? "Contact mis à jour" : "Contact ajouté" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement du contact" });
    }
    setContactSaving(false);
  };

  const handleContactSave = (contact: Contact | null, data: Partial<Contact>) => {
    const isPrimaryContact = contact?.is_primary || data.is_primary;
    if (isPrimaryContact && contact) {
      const emailChanged = data.email !== undefined && data.email !== contact.email;
      const phoneChanged = data.phone !== undefined && data.phone !== contact.phone;
      if (emailChanged || phoneChanged) {
        setPendingContactData({ contact, data });
        return;
      }
    }
    if (!contact && data.is_primary && (data.email || data.phone)) {
      setPendingContactData({ contact, data });
      return;
    }
    saveContactToDossier(contact, data);
  };

  const saveVehicleToDossier = async (vehicle: ClientVehicle, data: Partial<ClientVehicle>) => {
    if (!parentDossier) return;
    setVehicleSaving(true);
    try {
      const updatedVehicles = (parentDossier.vehicles || []).map((v) =>
        v.id === vehicle.id ? { ...v, ...data } : v
      );
      const updatedDossier = await dossiersApi.update(parentDossier.id, { vehicles: updatedVehicles } as any);
      setParentDossier(updatedDossier);
      // If this is the selected vehicle, update the form too
      if (form.vehicle_id === vehicle.id || form.vehicle?.id === vehicle.id) {
        const updated = updatedVehicles.find((v) => v.id === vehicle.id);
        if (updated) update("vehicle", updated as any);
      }
      setEditingVehicle(null);
      showNotification({ type: "success", message: "Véhicule modifié" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde du véhicule" });
    } finally {
      setVehicleSaving(false);
    }
  };

  const restitutionChanged =
    form.restitution?.seconds !== ordre.restitution?.seconds;

  const handleSave = async (restitutionComment?: string, notify?: boolean) => {
    setSaving(true);
    try {
      const updated = await ordresApi.update(ordre.id, {
        name: form.name,
        tags: form.tags,
        status: form.status,
        immatriculation: form.immatriculation,
        modele: form.modele,
        campagne: form.campagne,
        assigned_to: form.assigned_to,
        notes: form.notes,
        interventions: form.interventions,
        documents: form.documents,
        reception_date: form.reception_date,
        restitution: form.restitution,
      });
      // If restitution date changed, notify client (only if checkbox was checked)
      if (restitutionChanged && form.restitution?.seconds && notify !== false) {
        try {
          await ordresApi.notifyRestitutionChange(ordre.id, {
            old_date_seconds: ordre.restitution?.seconds,
            new_date_seconds: form.restitution.seconds,
            comment: restitutionComment || undefined,
            notify,
          });
        } catch (e) {
          console.error("Error notifying restitution change:", e);
        }
      }
      useOrdreStore.getState().updateOrdre(updated);
      // Refetch planning appointments so the grid reflects updated times
      const planningState = usePlanningStore.getState();
      if (planningState._fetchedOrgId) {
        planningState.fetchAppointments(planningState._fetchedOrgId, true);
      }
      // Invalidate cached car windows so atelier grid picks up new dates
      useAtelierStore.getState().invalidateCarWindows(ordre.id);
      onSave(updated);
      showNotification({ type: "success", message: "Ordre enregistré" });

      // Check if any interventions have assigned workers — offer auto-placement
      const assignedInterventions = form.interventions.filter((i) => i.assigned_to);
      if (assignedInterventions.length > 0) {
        setShowAutoPlace("confirm");
      }
    } catch (e) {
      console.error("Error saving ordre:", e);
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement" });
    } finally {
      setSaving(false);
      setShowRestitutionConfirm(false);
    }
  };

  const handleSaveClick = () => {
    if (restitutionChanged && form.restitution?.seconds) {
      setShowRestitutionConfirm(true);
    } else {
      handleSave();
    }
  };

  const handleAutoPlace = async () => {
    if (!orgData?.id) return;
    const assignedInterventions = form.interventions.filter((i) => i.assigned_to);
    if (assignedInterventions.length === 0) return;

    setAutoPlaceLoading(true);
    try {
      const results = await atelierApi.autoPlaceForOrdre({
        org_id: orgData.id,
        ordre_id: ordre.id,
        interventions: assignedInterventions.map((i) => ({
          intervention_id: i.id,
          label: i.label,
          assigned_to: i.assigned_to!,
          estimated_hours: i.estimated_hours || i.default_hours || 1,
        })),
      });
      setAutoPlaceResults(results);
      setShowAutoPlace("results");
      // Refresh atelier data
      fetchAtelierData(orgData.id);
    } catch (e) {
      console.error("Auto-place error:", e);
      showNotification({ type: "error", message: "Erreur lors du placement automatique" });
      setShowAutoPlace(null);
      onClose();
    } finally {
      setAutoPlaceLoading(false);
    }
  };

  return (
    <>
      <BaseModal
        title={<>Modifier RDV — {form.num} <span className="text-neutral-100 text-sm font-normal ml-2">{ordre.id}</span></>}
        startIcon={<Icon1 name="planning" size={20} />}
        onClose={onClose}
        width="max-w-4xl"
      >
        {/* Quick nav + Tabs */}
        <div className="px-4 pt-3 overflow-hidden flex flex-col gap-2">
          <div className="flex flex-row items-center gap-3">
            <button
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 w-fit cursor-pointer"
              onClick={() => { onClose(); navigateTo({ route: `/ordre/${ordre.id}` }); }}
            >
              <Icon1 name="open" size={14} />
              Ouvrir la fiche ordre
            </button>
            {ordre.dossier_id && (
              <button
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 w-fit cursor-pointer"
                onClick={() => { onClose(); navigateTo({ route: `/dossier/${ordre.dossier_id}` }); }}
              >
                <Icon1 name="open" size={14} />
                Ouvrir le dossier
              </button>
            )}
          </div>
          <ChunkyTabs
            activeTab={activeTab}
            tabs={TABS}
            setActiveTab={setActiveTab}
          />
        </div>

        {/* Tab content */}
        <div className="min-h-300 overflow-x-hidden">
          {activeTab === "ordre" && (
            <OrdreTab
              form={form}
              update={update}
              assignedWorkers={assignedWorkers}
              linkedVehicle={form.vehicle || parentDossier?.vehicles?.find((v) => v.id === form.vehicle_id) || null}
              dossierType={parentDossier?.type as DossierType | undefined}
              onDossierTypeChange={async (type) => {
                if (!parentDossier) return;
                const updated = await dossiersApi.update(parentDossier.id, { type: type || "particulier" } as any);
                setParentDossier(updated);
              }}
            />
          )}
          {activeTab === "interventions" && (
            <InterventionsTab
              interventions={form.interventions}
              interventionGroups={form.intervention_groups}
              atelierCharges={ordreAtelierCharges}
              onAdd={(g) => { setAddGroup(g); setSubModal("intervention"); }}
              onRemove={(idx) => update("interventions", form.interventions.filter((_, i) => i !== idx))}
            />
          )}
          {activeTab === "contacts" && (
            <ContactsTab
              contacts={form.contacts || []}
              onAdd={() => { setEditingContact(null); setSubModal("contact"); }}
              onEdit={(c) => { setEditingContact(c); setSubModal("contact"); }}
            />
          )}
          {activeTab === "vehicules" && (
            <VehiculesTab
              dossierVehicles={parentDossier?.vehicles || (form.vehicle ? [form.vehicle] : [])}
              selectedVehicleId={form.vehicle_id || form.vehicle?.id}
              onSelect={(vehicleId) => {
                const v = (parentDossier?.vehicles || []).find((veh) => veh.id === vehicleId);
                update("vehicle_id", vehicleId);
                if (v) update("vehicle", v);
              }}
              onEdit={(v) => setEditingVehicle(v)}
            />
          )}
          {activeTab === "documents" && (
            <DocumentsTab
              documents={parentDossier?.documents || form.documents || []}
              onAdd={() => setSubModal("document")}
              onPreview={openDocPreview}
            />
          )}
          {activeTab === "communications" && (
            <div style={{ height: 500 }}>
              <ChatPanel
                discussionId={parentDossier?.discussion_id || ordre.discussion_id}
                internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
                entityType="ordre"
                entityId={ordre.id}
                entityName={ordre.name}
                compact
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-1 p-4 flex flex-row justify-between">
          <div className="flex flex-row gap-2">
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              label="Ajouter un RDV secondaire"
              onClick={() => setSubModal("secondary")}
            />
            {onDelete && (
              <SecondaryButton
                variant="small"
                startIcon={<Icon1 name="delete" size={14} />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Supprimer
              </SecondaryButton>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="copy" size={14} />}
              onClick={() => {
                navigator.clipboard.writeText(ordre.id);
                showNotification({ type: "default", message: "ID ordre copié" });
              }}
            >
              Copier ID
            </SecondaryButton>
            <SecondaryButton variant="small" onClick={onClose}>
              Fermer
            </SecondaryButton>
            <Button variant="small" label={saving ? "Enregistrement..." : "Enregistrer"} onClick={handleSaveClick} disabled={saving || !form.restitution?.seconds} />
          </div>
        </div>

      </BaseModal>

      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer l'ordre ${form.num} ?`}
          message="Cette action est irréversible. L'ordre et ses rendez-vous associés seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            setDeleting(true);
            try {
              await ordresApi.delete(ordre.id);
              const planningState = usePlanningStore.getState();
              if (planningState._fetchedOrgId) {
                planningState.fetchAppointments(planningState._fetchedOrgId, true);
              }
              const atelierState = useAtelierStore.getState();
              if (atelierState._fetchedOrgId) {
                atelierState.fetchData(atelierState._fetchedOrgId, true);
              }
              showNotification({ type: "success", message: "Ordre supprimé" });
              onDelete?.(ordre.id);
              onClose();
            } catch {
              showNotification({ type: "error", message: "Erreur lors de la suppression" });
            }
            setDeleting(false);
          }}
          confirmText="CONFIRMER"
        />
      )}

      {/* Sub-modals */}
      {subModal === "intervention" && (
        <InterventionPickerModal
          configuredInterventions={configuredInterventions}
          onCatalogUpdated={setConfiguredInterventions}
          group={addGroup}
          onAdd={(inter) => update("interventions", [...form.interventions, inter])}
          onClose={() => setSubModal(null)}
        />
      )}
      {subModal === "contact" && (
        <ContactFormModal
          contact={editingContact}
          onClose={() => { setSubModal(null); setEditingContact(null); }}
          onSave={(data) => handleContactSave(editingContact, data)}
          saving={contactSaving}
        />
      )}
      {pendingContactData && (
        <ConfirmationModal
          title="Mettre à jour les coordonnées ?"
          message="Les coordonnées de ce contact principal sont utilisées pour les notifications, e-mails et messages WhatsApp. Confirmez-vous la modification ?"
          confirmLabel="Oui, mettre à jour"
          onClose={() => setPendingContactData(null)}
          onConfirm={() => { saveContactToDossier(pendingContactData.contact, pendingContactData.data); }}
        />
      )}
      {subModal === "vehicle" && <AddVehicleModal onClose={() => setSubModal(null)} />}
      {editingVehicle && (
        <VehicleEditModal
          vehicle={editingVehicle as ClientVehicle}
          onSave={(data) => saveVehicleToDossier(editingVehicle as ClientVehicle, data)}
          onClose={() => setEditingVehicle(null)}
          saving={vehicleSaving}
          timeline={parentDossier?.timeline || []}
          ordres={parentDossier?.ordres || []}
        />
      )}
      {subModal === "document" && ordre.dossier_id && (
        <AddDocumentModal
          dossierId={ordre.dossier_id}
          onClose={() => setSubModal(null)}
          onSuccess={(updatedDossier) => setParentDossier(updatedDossier)}
        />
      )}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => {
            const a = document.createElement("a");
            a.href = previewDoc.url;
            a.download = previewDoc.doc.name;
            a.target = "_blank";
            a.click();
          }}
        />
      )}
      {showRestitutionConfirm && form.restitution?.seconds && (
        <RestitutionChangeConfirmModal
          oldSeconds={ordre.restitution?.seconds}
          newSeconds={form.restitution.seconds}
          onClose={() => setShowRestitutionConfirm(false)}
          onConfirm={(comment, notify) => handleSave(comment, notify)}
          saving={saving}
        />
      )}
      {subModal === "secondary" && (
        <SecondaryOrdreModal
          ordre={ordre}
          onClose={() => { setSubModal(null); onClose(); }}
        />
      )}

      {/* Auto-place confirmation */}
      {showAutoPlace === "confirm" && (
        <BaseModal
          title="Planification atelier"
          startIcon={<Icon1 name="planning" size={20} />}
          onClose={() => { setShowAutoPlace(null); onClose(); }}
        >
          <div className="p-4 flex flex-col gap-3">
            <p className="text-h3 text-neutral-700">
              {form.interventions.filter((i) => i.assigned_to).length} intervention(s) assignée(s).
              Placer automatiquement sur le planning atelier ?
            </p>
            <div className="text-h4 text-neutral-400">
              Le système trouvera le meilleur créneau disponible pour chaque mécanicien assigné,
              en respectant les disponibilités et la charge existante.
            </div>
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={() => { setShowAutoPlace(null); onClose(); }}>
              Non merci
            </SecondaryButton>
            <Button
              variant="small"
              label={autoPlaceLoading ? "Placement en cours..." : "Placer automatiquement"}
              onClick={handleAutoPlace}
              disabled={autoPlaceLoading}
            />
          </div>
        </BaseModal>
      )}

      {/* Auto-place results */}
      {showAutoPlace === "results" && autoPlaceResults && (
        <BaseModal
          title="Résultat du placement"
          startIcon={<Icon1 name="planning" size={20} />}
          onClose={() => { setShowAutoPlace(null); onClose(); }}
        >
          <div className="p-4 flex flex-col gap-4">
            {autoPlaceResults.placed.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h3 font-medium text-green-700">
                  {autoPlaceResults.placed.length} intervention(s) placée(s)
                </div>
                {autoPlaceResults.placed.map((r) => (
                  <div key={r.intervention_id} className="flex flex-col bg-green-50 rounded-lg px-3 py-2">
                    <span className="text-h3 font-medium">{r.label}</span>
                    <span className="text-h4 text-neutral-600">
                      {r.worker_name} — {r.date ? new Date(r.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : ""} de {formatHour(r.start_hour || 0)} à {formatHour((r.start_hour || 0) + (r.duration_hours || 0))}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {autoPlaceResults.unplaced.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h3 font-medium text-orange-600">
                  {autoPlaceResults.unplaced.length} intervention(s) sans créneau
                </div>
                {autoPlaceResults.unplaced.map((r) => (
                  <div key={r.intervention_id} className="flex flex-col bg-orange-50 rounded-lg px-3 py-2">
                    <span className="text-h3 font-medium">{r.label}</span>
                    <span className="text-h4 text-neutral-600">{r.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end">
            <Button variant="small" label="Fermer" onClick={() => { setShowAutoPlace(null); onClose(); }} />
          </div>
        </BaseModal>
      )}
    </>
  );
}
